import { apiRequest } from "@/lib/api/http";
import { isCurrencyCode, type CurrencyCode } from "@/lib/format/money";

/**
 * Per-user settings stored upstream (`UserPreferences.php`, `docs/USER_PREFERENCES_SERVER.md`),
 * so they follow the user across devices — unlike the UI language, which is per browser.
 */
export interface UserPreferences {
  /** `null` when upstream sent none or an unknown code; callers fall back to `DEFAULT_CURRENCY`. */
  defaultCurrency: CurrencyCode | null;
}

interface RemoteUserPreferences {
  default_currency?: string | null;
}

interface PreferencesResponse {
  success: true;
  data?: RemoteUserPreferences | null;
}

function mapPreferences(remote: RemoteUserPreferences | null | undefined): UserPreferences {
  const currency = remote?.default_currency?.trim().toUpperCase();
  return { defaultCurrency: isCurrencyCode(currency) ? currency : null };
}

export async function getUserPreferences(): Promise<UserPreferences> {
  const response = await apiRequest<PreferencesResponse>("/api/user-preferences", {
    method: "GET",
    cache: "no-store",
  });
  return mapPreferences(response.data);
}

/** Partial: only the fields passed are sent. Returns the full, updated preferences. */
export async function updateUserPreferences(
  changes: Partial<{ defaultCurrency: CurrencyCode }>,
): Promise<UserPreferences> {
  const response = await apiRequest<PreferencesResponse>("/api/user-preferences", {
    method: "PUT",
    body: JSON.stringify({
      ...(changes.defaultCurrency !== undefined ? { default_currency: changes.defaultCurrency } : {}),
    }),
  });
  return mapPreferences(response.data);
}
